/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Subscription Plans Configuration
 *
 * Defines all subscription plans, pricing, features, and limits
 * for the Contractor Co-Pilot platform.
 */

// ============================================================================
// SUBSCRIPTION PLAN TYPES
// ============================================================================

export const SUBSCRIPTION_PLANS = {
  /** @deprecated Legacy — new signups use real plans with Stripe trial_period_days */
  TRIAL: "trial_org",
  /** Mobile-first for field contractors */
  MOBILE: "mobile_plan",
  /** Full platform for solo contractors */
  STARTER: "starter_plan",
  /** Growing teams with QuickBooks */
  PROFESSIONAL: "professional_plan",
  /** Multi-location with API access */
  BUSINESS: "business_plan",
} as const;

/** Trial duration in days */
export const TRIAL_DURATION_DAYS = 7;

export type SubscriptionPlan =
  (typeof SUBSCRIPTION_PLANS)[keyof typeof SUBSCRIPTION_PLANS];

// Define the limits type focused on actual resource consumption
// Note: All limits are per-organization (shared across all users in the organization)
export interface PlanLimits {
  // Dollar-denominated AI budget (in cents) — 3x markup on API costs
  // Users see this as their AI budget and can freely choose model tiers
  readonly ai_budget_cents: number; // e.g. 1600 = $16.00

  readonly storage_gb: number; // 1 to -1 (unlimited)
  readonly api_requests_per_month: number; // 100 to -1 (unlimited)

  // Business limits - not technical restrictions but plan differentiators
  readonly users_per_organization: number; // 1 for free/mobile, -1 for unlimited (per-seat billing)
}

// ============================================================================
// DOCUMENT UPLOAD LIMITS (AI cost protection)
// ============================================================================
// Large documents cost more to process with AI, so we limit by plan

export interface DocumentLimits {
  /** Maximum pages per document */
  readonly maxPages: number;
  /** Maximum documents per month (-1 = unlimited) */
  readonly maxDocsPerMonth: number;
  /** Maximum file size in bytes */
  readonly maxFileSizeBytes: number;
}

export const PLAN_DOCUMENT_LIMITS: Record<SubscriptionPlan, DocumentLimits> = {
  [SUBSCRIPTION_PLANS.TRIAL]: {
    maxPages: 25, // Same as Starter during trial
    maxDocsPerMonth: 50, // Limited to encourage conversion
    maxFileSizeBytes: 10 * 1024 * 1024, // 10MB
  },
  [SUBSCRIPTION_PLANS.MOBILE]: {
    maxPages: 10,
    maxDocsPerMonth: 25,
    maxFileSizeBytes: 5 * 1024 * 1024, // 5MB
  },
  [SUBSCRIPTION_PLANS.STARTER]: {
    maxPages: 25,
    maxDocsPerMonth: 100,
    maxFileSizeBytes: 10 * 1024 * 1024, // 10MB
  },
  [SUBSCRIPTION_PLANS.PROFESSIONAL]: {
    maxPages: 50,
    maxDocsPerMonth: 300,
    maxFileSizeBytes: 25 * 1024 * 1024, // 25MB
  },
  [SUBSCRIPTION_PLANS.BUSINESS]: {
    maxPages: 100,
    maxDocsPerMonth: 500,
    maxFileSizeBytes: 50 * 1024 * 1024, // 50MB
  },
};

// ============================================================================
// SUBSCRIPTION PLAN CONFIGURATION
// ============================================================================

export const SUBSCRIPTION_PLAN_CONFIG = {
  // ============================================================================
  // TRIAL TIER - 7-day trial with Starter features
  // ============================================================================
  [SUBSCRIPTION_PLANS.TRIAL]: {
    name: "Free Trial",
    description: "7-day trial with full Starter features.",
    monthlyPrice: 0,
    annualPrice: 0,
    billingCycle: "monthly" as const,
    features: [
      "7-day full access trial",
      "$3.00 AI budget included",
      "Unlimited projects during trial",
      "All Starter features included",
      "AI-powered estimate generation",
      "Professional proposal builder",
      "10GB storage",
    ] as string[],
    limits: {
      ai_budget_cents: 300, // $3.00 fixed trial budget
      storage_gb: 10,
      api_requests_per_month: 500,
      users_per_organization: 1,
    } satisfies PlanLimits,
    popular: false,
    recommended: false,
    /** Trial-specific: duration in days */
    trialDurationDays: 7,
  },

  // ============================================================================
  // MOBILE TIER - $29/mo ($24/mo annual) - Entry Point "Land & Expand"
  // Target: Solo contractors who work in the field
  // ============================================================================
  [SUBSCRIPTION_PLANS.MOBILE]: {
    name: "Mobile",
    description:
      "For contractors on the go. Photo-to-estimate, voice notes, and quick quotes from your phone. Coming soon.",
    monthlyPrice: 29,
    annualPrice: 24,
    billingCycle: "monthly" as const,
    features: [
      "$9.00/mo AI budget",
      "Mobile app with full features",
      "Photo-to-estimate AI",
      "Voice notes & transcription",
      "Quick quote generation",
      "Client portal sharing",
      "Basic templates",
      "Email support",
      "5GB storage",
    ] as string[],
    limits: {
      ai_budget_cents: 900, // $9.00 — 1.5x increase for quality
      storage_gb: 5,
      api_requests_per_month: 500,
      users_per_organization: 1,
    } satisfies PlanLimits,
    popular: false,
    recommended: false,
  },

  // ============================================================================
  // STARTER TIER - $79/mo ($66/mo annual)
  // Target: Solo contractors ready for full platform
  // ============================================================================
  [SUBSCRIPTION_PLANS.STARTER]: {
    name: "Starter",
    description:
      "Full estimating platform for solo contractors. Everything you need to create professional estimates and proposals.",
    monthlyPrice: 79,
    annualPrice: 66,
    billingCycle: "monthly" as const,
    features: [
      "$32.00/mo AI budget",
      "Full web platform access",
      "Unlimited projects & estimates",
      "AI-powered estimate generation",
      "Professional proposal builder",
      "Document management",
      "Basic reporting & analytics",
      "Excel import/export",
      "Standard templates library",
      "Email support (24hr response)",
      "10GB storage",
    ] as string[],
    limits: {
      ai_budget_cents: 3200, // $32.00 — 2x increase for quality
      storage_gb: 10,
      api_requests_per_month: 1000,
      users_per_organization: 1,
    } satisfies PlanLimits,
    popular: true,
    recommended: true,
  },

  // ============================================================================
  // PROFESSIONAL TIER - $149/mo ($124/mo annual)
  // Target: Small teams, established contractors
  // ============================================================================
  [SUBSCRIPTION_PLANS.PROFESSIONAL]: {
    name: "Professional",
    description:
      "For growing teams. Advanced AI analysis and team collaboration tools.",
    monthlyPrice: 149,
    annualPrice: 124,
    billingCycle: "monthly" as const,
    features: [
      "$60.00/mo AI budget",
      "Everything in Starter",
      "Advanced AI cost analysis",
      "AI proposal generation",
      "Team collaboration (up to 5 users)",
      "Custom estimate templates",
      "Advanced reporting & analytics",
      "Branded proposal sharing",
      "Priority email support",
      "50GB storage",
    ] as string[],
    limits: {
      ai_budget_cents: 6000, // $60.00 — 2x increase for quality
      storage_gb: 50,
      api_requests_per_month: 5000,
      users_per_organization: 5,
    } satisfies PlanLimits,
    popular: false,
    recommended: false,
  },

  // ============================================================================
  // BUSINESS TIER - $299/mo ($249/mo annual)
  // Target: Multi-crew operations, specialty contractors
  // ============================================================================
  [SUBSCRIPTION_PLANS.BUSINESS]: {
    name: "Business",
    description:
      "For established businesses. Division management, API access, and advanced permissions.",
    monthlyPrice: 299,
    annualPrice: 249,
    billingCycle: "monthly" as const,
    features: [
      "$120.00/mo AI budget",
      "Everything in Professional",
      "Division management",
      "API access for integrations",
      "Advanced permissions & roles",
      "Blueprint/takeoff analysis (coming soon)",
      "Priority email support",
      "100GB storage",
    ] as string[],
    limits: {
      ai_budget_cents: 12000, // $120.00 — 2x increase for quality
      storage_gb: 100,
      api_requests_per_month: 25000,
      users_per_organization: 10,
    } satisfies PlanLimits,
    popular: false,
    recommended: false,
  },
} as const;

// ============================================================================
// SEAT CAPS (max active users per plan) – used for onboarding seat counter
// ============================================================================

export const MAX_SEATS_PER_PLAN: Record<SubscriptionPlan, number> = {
  [SUBSCRIPTION_PLANS.TRIAL]: 1,
  [SUBSCRIPTION_PLANS.MOBILE]: 1,
  [SUBSCRIPTION_PLANS.STARTER]: 1,
  [SUBSCRIPTION_PLANS.PROFESSIONAL]: 5,
  [SUBSCRIPTION_PLANS.BUSINESS]: 10,
};

/**
 * Office + field seat pool limits per plan.
 *
 * Office pool: Owner, Admin, Manager, Salesman, Estimator, Viewer
 * Field pool: Crew Leader, Laborer
 *
 * Locked by the tier matrix v3 (mobile-tier-matrix.md, 2026-04-13).
 * Stripe webhook handlers set these on tier change.
 */
export const SEAT_POOLS_PER_PLAN: Record<
  SubscriptionPlan,
  { office: number; field: number }
> = {
  [SUBSCRIPTION_PLANS.TRIAL]: { office: 1, field: 0 },
  [SUBSCRIPTION_PLANS.MOBILE]: { office: 1, field: 0 },
  [SUBSCRIPTION_PLANS.STARTER]: { office: 1, field: 0 },
  [SUBSCRIPTION_PLANS.PROFESSIONAL]: { office: 5, field: 10 },
  [SUBSCRIPTION_PLANS.BUSINESS]: { office: 15, field: 30 },
};

/** Label for users limit in UI: "1", "5", "10" */
export function getActiveUsersLabel(plan: SubscriptionPlan): string {
  const config = SUBSCRIPTION_PLAN_CONFIG[plan];
  if (!config) return "1";
  return String(config.limits.users_per_organization);
}

// ============================================================================
// SUBSCRIPTION PLAN DISPLAY HELPERS
// ============================================================================

export const getPlanDisplayName = (plan: SubscriptionPlan): string => {
  return SUBSCRIPTION_PLAN_CONFIG[plan].name;
};

export const getPlanDescription = (plan: SubscriptionPlan): string => {
  return SUBSCRIPTION_PLAN_CONFIG[plan].description;
};

export const getPlanPrice = (
  plan: SubscriptionPlan,
  annual: boolean = false,
): number => {
  const config = SUBSCRIPTION_PLAN_CONFIG[plan];
  return annual ? config.annualPrice : config.monthlyPrice;
};

export const getPlanFeatures = (plan: SubscriptionPlan): string[] => {
  return [...SUBSCRIPTION_PLAN_CONFIG[plan].features]; // Return mutable copy
};

export const getPlanLimits = (plan: SubscriptionPlan): PlanLimits => {
  return SUBSCRIPTION_PLAN_CONFIG[plan].limits;
};

export const isPlanPopular = (plan: SubscriptionPlan): boolean => {
  return SUBSCRIPTION_PLAN_CONFIG[plan].popular;
};

export const isPlanRecommended = (plan: SubscriptionPlan): boolean => {
  return SUBSCRIPTION_PLAN_CONFIG[plan].recommended;
};

// ============================================================================
// SUBSCRIPTION PLAN VALIDATION
// ============================================================================

export const isValidSubscriptionPlan = (
  plan: string,
): plan is SubscriptionPlan => {
  return Object.values(SUBSCRIPTION_PLANS).includes(plan as SubscriptionPlan);
};

export const getAvailablePlans = (): SubscriptionPlan[] => {
  return Object.values(SUBSCRIPTION_PLANS);
};

// ============================================================================
// USAGE LIMIT HELPERS
// ============================================================================

export const checkUsageLimit = (
  plan: SubscriptionPlan,
  feature: keyof PlanLimits,
  currentUsage: number,
): boolean => {
  const limits = getPlanLimits(plan);
  const limit = limits[feature];

  // -1 means unlimited
  if (limit === -1) return true;

  return currentUsage < limit;
};

export const getRemainingUsage = (
  plan: SubscriptionPlan,
  feature: keyof PlanLimits,
  currentUsage: number,
): number => {
  const limits = getPlanLimits(plan);
  const limit = limits[feature];

  // -1 means unlimited
  if (limit === -1) return -1;

  return Math.max(0, limit - currentUsage);
};

// ============================================================================
// BILLING CYCLE HELPERS
// ============================================================================

export const BILLING_CYCLES = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
} as const;

export type BillingCycle = (typeof BILLING_CYCLES)[keyof typeof BILLING_CYCLES];

export const getBillingCycleDisplayName = (cycle: BillingCycle): string => {
  return cycle === BILLING_CYCLES.MONTHLY ? "Monthly" : "Annual";
};

export const getBillingCycleSavings = (plan: SubscriptionPlan): number => {
  const config = SUBSCRIPTION_PLAN_CONFIG[plan];
  const monthlyTotal = config.monthlyPrice * 12;
  const annualTotal = config.annualPrice * 12;
  return monthlyTotal - annualTotal;
};

export const getBillingCycleSavingsPercentage = (
  plan: SubscriptionPlan,
): number => {
  const config = SUBSCRIPTION_PLAN_CONFIG[plan];
  if (config.monthlyPrice === 0) return 0;

  const savings = getBillingCycleSavings(plan);
  const monthlyTotal = config.monthlyPrice * 12;
  return Math.round((savings / monthlyTotal) * 100);
};

// ============================================================================
// TRIAL HELPERS
// ============================================================================

/**
 * Check if a plan is the trial plan
 */
export const isTrialPlan = (plan: SubscriptionPlan): boolean => {
  return plan === SUBSCRIPTION_PLANS.TRIAL;
};

/**
 * Calculate trial end date from start date
 */
export const calculateTrialEndDate = (startDate: Date = new Date()): Date => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + TRIAL_DURATION_DAYS);
  return endDate;
};

/**
 * Check if a trial has expired
 */
export const isTrialExpired = (trialEndDate: string | Date | null): boolean => {
  if (!trialEndDate) return false;
  const endDate =
    typeof trialEndDate === "string" ? new Date(trialEndDate) : trialEndDate;
  return new Date() > endDate;
};

/**
 * Get days remaining in trial
 */
export const getTrialDaysRemaining = (
  trialEndDate: string | Date | null,
): number => {
  if (!trialEndDate) return 0;
  const endDate =
    typeof trialEndDate === "string" ? new Date(trialEndDate) : trialEndDate;
  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

/**
 * Get list of paid plans (excludes trial)
 */
export const getPaidPlans = (): SubscriptionPlan[] => {
  return Object.values(SUBSCRIPTION_PLANS).filter(
    (plan) => plan !== SUBSCRIPTION_PLANS.TRIAL,
  );
};
